<template>
  <div class="stories-wrapper">
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1>Success Stories</h1>
        <p>Manage creator success stories and their published content.</p>
      </div>

      <button class="primary-btn" @click="openAddModal">
        <i class="bx bx-plus"></i>
        Add Story
      </button>
    </div>

    <!-- Stats -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon blue">
          <i class="bx bx-trophy"></i>
        </div>

        <div>
          <span>Total Stories</span>
          <strong>{{ totalStories }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon green">
          <i class="bx bx-user"></i>
        </div>

        <div>
          <span>Creators</span>
          <strong>{{ creatorCount }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon orange">
          <i class="bx bx-video"></i>
        </div>

        <div>
          <span>Video Stories</span>
          <strong>{{ videoStories }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon purple">
          <i class="bx bx-headphone"></i>
        </div>

        <div>
          <span>Total Listeners</span>
          <strong>{{ totalListeners }}</strong>
        </div>
      </div>
    </div>

    <!-- Filters / List -->
    <div class="content-card">
      <div class="toolbar">
        <div class="search-box">
          <i class="bx bx-search"></i>

          <input
            v-model="search"
            type="text"
            placeholder="Search stories, niche or handle..."
          />
        </div>

        <select
          v-model="typeFilter"
          class="filter-select"
        >
          <option value="">All Types</option>
          <option value="Video">Video</option>
          <option value="Podcast">Podcast</option>
          <option value="Article">Article</option>
          <option value="Interview">Interview</option>
        </select>

        <select
          v-model="nicheFilter"
          class="filter-select"
        >
          <option value="">All Niches</option>

          <option
            v-for="niche in niches"
            :key="niche"
            :value="niche"
          >
            {{ niche }}
          </option>
        </select>

        <button
          class="reset-btn"
          @click="resetFilters"
        >
          <i class="bx bx-reset"></i>
          Reset
        </button>
      </div>

      <!-- Table -->
      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Story</th>
              <th>Niche</th>
              <th>Type</th>
              <th>Creator</th>
              <th>Listeners</th>
              <th>Price</th>
              <th>Media</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="paginatedStories.length === 0">
              <td
                colspan="9"
                class="empty-state"
              >
                <i class="bx bx-book-content"></i>

                <h3>No stories found</h3>

                <p>
                  Try changing your search or filters.
                </p>
              </td>
            </tr>

            <tr
              v-for="story in paginatedStories"
              :key="story.story_id"
            >
              <!-- Story -->
              <td>
                <div class="story-info">
                  <div class="story-image">
                    <img
                      v-if="story.story_img"
                      :src="story.story_img"
                      :alt="story.story_title"
                    />

                    <i
                      v-else
                      class="bx bx-book-open"
                    ></i>
                  </div>

                  <div class="story-details">
                    <strong>
                      {{ story.story_title }}
                    </strong>

                    <small>
                      #{{ story.story_id }}

                      <span
                        v-if="story.story_handle"
                      >
                        · {{ story.story_handle }}
                      </span>
                    </small>
                  </div>
                </div>
              </td>

              <!-- Niche -->
              <td>
                <span class="niche-badge">
                  {{ story.story_niche || "—" }}
                </span>
              </td>

              <!-- Type -->
              <td>
                <span class="type-badge">
                  {{ story.story_typ || "—" }}
                </span>
              </td>

              <!-- Creator -->
              <td>
                <div class="creator-info">
                  <strong>
                    {{ story.creator_name || "—" }}
                  </strong>

                  <small
                    v-if="story.story_creator"
                  >
                    Creator #{{ story.story_creator }}
                  </small>
                </div>
              </td>

              <!-- Listeners -->
              <td>
                <span class="listeners">
                  <i class="bx bx-headphone"></i>
                  {{ story.story_listners || "—" }}
                </span>
              </td>

              <!-- Price -->
              <td>
                <strong class="price">
                  {{
                    story.story_price
                      ? "₹" +
                        Number(
                          story.story_price
                        ).toLocaleString("en-IN")
                      : "Free"
                  }}
                </strong>
              </td>

              <!-- Media -->
              <td>
                <div class="media-list">
                  <span
                    v-if="story.story_img"
                    title="Image"
                  >
                    <i class="bx bx-image"></i>
                  </span>

                  <span
                    v-if="story.story_video"
                    title="Video"
                  >
                    <i class="bx bx-video"></i>
                  </span>

                  <span
                    v-if="
                      !story.story_img &&
                      !story.story_video
                    "
                    class="no-media"
                  >
                    —
                  </span>
                </div>
              </td>

              <!-- Created -->
              <td>
                <span class="date-text">
                  {{ formatDate(story.story_datetime) }}
                </span>
              </td>

              <!-- Actions -->
              <td>
                <div class="actions">
                  <button
                    class="icon-btn view"
                    title="View"
                    @click="viewStory(story)"
                  >
                    <i class="bx bx-show"></i>
                  </button>

                  <button
                    class="icon-btn edit"
                    title="Edit"
                    @click="editStory(story)"
                  >
                    <i class="bx bx-edit-alt"></i>
                  </button>

                  <button
                    class="icon-btn delete"
                    title="Delete"
                    @click="confirmDelete(story)"
                  >
                    <i class="bx bx-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div
        class="pagination"
        v-if="totalPages > 1"
      >
        <span>
          Showing {{ startItem }}–{{ endItem }}
          of {{ totalStories }}
        </span>

        <div class="pagination-buttons">
          <button
            :disabled="currentPage === 1"
            @click="previousPage"
          >
            <i class="bx bx-chevron-left"></i>
          </button>

          <button
            v-for="page in totalPages"
            :key="page"
            :class="{
              active: currentPage === page
            }"
            @click="goToPage(page)"
          >
            {{ page }}
          </button>

          <button
            :disabled="
              currentPage === totalPages
            "
            @click="nextPage"
          >
            <i class="bx bx-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Add / Edit Modal -->
    <div
      v-if="showFormModal"
      class="modal-overlay"
      @click.self="closeFormModal"
    >
      <div class="modal">
        <div class="modal-header">
          <div>
            <h2>
              {{
                isEditing
                  ? "Edit Story"
                  : "Add Story"
              }}
            </h2>

            <p>
              {{
                isEditing
                  ? "Update success story information."
                  : "Create a new success story."
              }}
            </p>
          </div>

          <button
            class="close-btn"
            @click="closeFormModal"
          >
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="form-grid">

            <!-- Story Title -->
            <div class="form-group full">
              <label>
                Story Title <span>*</span>
              </label>

              <input
                v-model="form.story_title"
                type="text"
                placeholder="Enter story title"
              />
            </div>

            <!-- Parent Type -->
            <div class="form-group">
              <label>Parent Type</label>

              <select
                v-model="form.story_parent_type"
              >
                <option value="">
                  Select Parent Type
                </option>

                <option value="Home">
                  Home
                </option>

                <option value="Course">
                  Course
                </option>

                <option value="Creator">
                  Creator
                </option>

                <option value="Blog">
                  Blog
                </option>
              </select>
            </div>

            <!-- Course Parent -->
            <div
              v-if="
                form.story_parent_type ===
                'Course'
              "
              class="form-group"
            >
              <label>Course</label>

              <select
                v-model.number="
                  form.story_parent
                "
              >
                <option :value="null">
                  Select Course
                </option>

                <option
                  v-for="course in courses"
                  :key="course.course_id"
                  :value="course.course_id"
                >
                  {{
                    course.title ||
                    course.Title ||
                    "Course #" +
                      course.course_id
                  }}
                </option>
              </select>
            </div>

            <!-- Creator Parent -->
            <div
              v-if="
                form.story_parent_type ===
                'Creator'
              "
              class="form-group"
            >
              <label>Creator</label>

              <select
                v-model.number="
                  form.story_parent
                "
              >
                <option :value="null">
                  Select Creator
                </option>

                <option
                  v-for="creator in creators"
                  :key="creator.creator_id"
                  :value="creator.creator_id"
                >
                  {{
                    creator.cre_username ||
                    creator.Cre_username ||
                    "Creator #" +
                      creator.creator_id
                  }}
                </option>
              </select>
            </div>

            <!-- Blog Parent -->
            <div
              v-if="
                form.story_parent_type ===
                'Blog'
              "
              class="form-group"
            >
              <label>Blog</label>

              <select
                v-model.number="
                  form.story_parent
                "
              >
                <option :value="null">
                  Select Blog
                </option>

                <option
                  v-for="blog in blogs"
                  :key="blog.blog_id"
                  :value="blog.blog_id"
                >
                  {{
                    blog.blog_title ||
                    blog.Blog_title ||
                    "Blog #" +
                      blog.blog_id
                  }}
                </option>
              </select>
            </div>

            <!-- Niche -->
            <div class="form-group">
              <label>Niche</label>

              <input
                v-model="form.story_niche"
                type="text"
                placeholder="e.g. Technology"
              />
            </div>

            <!-- Handle -->
            <div class="form-group">
              <label>Handle</label>

              <input
                v-model="form.story_handle"
                type="text"
                placeholder="@creatorhandle"
              />
            </div>

            <!-- Story Type -->
            <div class="form-group">
              <label>Story Type</label>

              <select
                v-model="form.story_typ"
              >
                <option value="">
                  Select Type
                </option>

                <option value="Video">
                  Video
                </option>

                <option value="Podcast">
                  Podcast
                </option>

                <option value="Article">
                  Article
                </option>

                <option value="Interview">
                  Interview
                </option>
              </select>
            </div>

            <!-- Listeners -->
            <div class="form-group">
              <label>Listeners</label>

              <input
                v-model="form.story_listners"
                type="text"
                placeholder="e.g. 25K"
              />
            </div>

            <!-- Price -->
            <div class="form-group">
              <label>Price</label>

              <input
                v-model.number="form.story_price"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>

            <!-- Creator -->
            <div class="form-group">
              <label>Creator</label>

              <select
                v-model.number="
                  form.story_creator
                "
              >
                <option :value="null">
                  Select Creator
                </option>

                <option
                  v-for="creator in creators"
                  :key="
                    'story-creator-' +
                    creator.creator_id
                  "
                  :value="creator.creator_id"
                >
                  {{
                    creator.cre_username ||
                    creator.Cre_username ||
                    "Creator #" +
                      creator.creator_id
                  }}
                </option>
              </select>
            </div>

            <!-- Image -->
            <div class="form-group full">
              <label>Image URL</label>

              <input
                v-model="form.story_img"
                type="text"
                placeholder="https://example.com/story-image.jpg"
              />
            </div>

            <!-- Video -->
            <div class="form-group full">
              <label>Video URL</label>

              <input
                v-model="form.story_video"
                type="text"
                placeholder="https://example.com/story-video.mp4"
              />
            </div>

            <!-- Meta Title -->
            <div class="form-group">
              <label>Meta Title</label>

              <input
                v-model="form.story_meta_title"
                type="text"
                placeholder="SEO meta title"
              />
            </div>

            <!-- Keywords -->
            <div class="form-group">
              <label>Keywords</label>

              <input
                v-model="form.story_keyword"
                type="text"
                placeholder="success, creator, story"
              />
            </div>

          </div>
        </div>

        <div class="modal-footer">
          <button
            class="secondary-btn"
            @click="closeFormModal"
          >
            Cancel
          </button>

          <button
            class="primary-btn"
            @click="saveStory"
          >
            <i class="bx bx-check"></i>

            {{
              isEditing
                ? "Update Story"
                : "Create Story"
            }}
          </button>
        </div>
      </div>
    </div>

    <!-- View Modal -->
    <div
      v-if="showViewModal"
      class="modal-overlay"
      @click.self="
        showViewModal = false
      "
    >
      <div class="modal view-modal">
        <div class="modal-header">
          <div>
            <h2>Story Details</h2>

            <p>
              Complete success story information.
            </p>
          </div>

          <button
            class="close-btn"
            @click="
              showViewModal = false
            "
          >
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div
          class="modal-body"
          v-if="selectedStory"
        >
          <!-- Story Header -->
          <div class="story-profile">
            <div class="large-story-image">
              <img
                v-if="selectedStory.story_img"
                :src="
                  selectedStory.story_img
                "
                :alt="
                  selectedStory.story_title
                "
              />

              <i
                v-else
                class="bx bx-book-open"
              ></i>
            </div>

            <div>
              <h3>
                {{ selectedStory.story_title }}
              </h3>

              <p>
                Story #{{ selectedStory.story_id }}
              </p>

              <div class="story-badges">
                <span class="type-badge">
                  {{
                    selectedStory.story_typ ||
                    "—"
                  }}
                </span>

                <span class="niche-badge">
                  {{
                    selectedStory.story_niche ||
                    "—"
                  }}
                </span>
              </div>
            </div>
          </div>

          <!-- Story Information -->
          <div class="detail-section">
            <h4>Story Information</h4>

            <div class="details-grid">
              <div class="detail-item">
                <span>Handle</span>

                <strong>
                  {{
                    selectedStory.story_handle ||
                    "—"
                  }}
                </strong>
              </div>

              <div class="detail-item">
                <span>Listeners</span>

                <strong>
                  {{
                    selectedStory.story_listners ||
                    "—"
                  }}
                </strong>
              </div>

              <div class="detail-item">
                <span>Price</span>

                <strong>
                  {{
                    selectedStory.story_price
                      ? "₹" +
                        Number(
                          selectedStory.story_price
                        ).toLocaleString("en-IN")
                      : "Free"
                  }}
                </strong>
              </div>

              <div class="detail-item">
                <span>Parent Type</span>

                <strong>
                  {{
                    selectedStory.story_parent_type ||
                    "—"
                  }}
                </strong>
              </div>

              <div class="detail-item">
                <span>Parent</span>

                <strong>
                  {{
                    selectedStory.related_name ||
                    (
                      selectedStory.story_parent_type ===
                      "Home"
                        ? "Home"
                        : selectedStory.story_parent ||
                          "—"
                    )
                  }}
                </strong>
              </div>

              <div class="detail-item">
                <span>Creator ID</span>

                <strong>
                  {{
                    selectedStory.story_creator ||
                    "—"
                  }}
                </strong>
              </div>

              <div class="detail-item">
                <span>Created</span>

                <strong>
                  {{
                    formatDate(
                      selectedStory.story_datetime
                    )
                  }}
                </strong>
              </div>

              <div class="detail-item">
                <span>Story ID</span>

                <strong>
                  #{{ selectedStory.story_id }}
                </strong>
              </div>
            </div>
          </div>

          <!-- SEO -->
          <div class="detail-section">
            <h4>SEO Information</h4>

            <div class="details-grid">
              <div class="detail-item">
                <span>Meta Title</span>

                <strong>
                  {{
                    selectedStory.story_meta_title ||
                    "—"
                  }}
                </strong>
              </div>

              <div class="detail-item">
                <span>Keywords</span>

                <strong>
                  {{
                    selectedStory.story_keyword ||
                    "—"
                  }}
                </strong>
              </div>
            </div>
          </div>

          <!-- Media -->
          <div class="detail-section">
            <h4>Media</h4>

            <div class="media-preview">
              <div
                v-if="selectedStory.story_img"
              >
                <span>Image</span>

                <img
                  :src="
                    selectedStory.story_img
                  "
                  alt="Success Story"
                />
              </div>

              <a
                v-if="
                  selectedStory.story_video
                "
                :href="
                  selectedStory.story_video
                "
                target="_blank"
                class="video-link"
              >
                <i class="bx bx-play-circle"></i>
                View Video
              </a>

              <span
                v-if="
                  !selectedStory.story_img &&
                  !selectedStory.story_video
                "
                class="no-data"
              >
                No media attached.
              </span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            class="secondary-btn"
            @click="
              showViewModal = false
            "
          >
            Close
          </button>

          <button
            class="primary-btn"
            @click="
              showViewModal = false;
              editStory(selectedStory);
            "
          >
            <i class="bx bx-edit-alt"></i>
            Edit Story
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Modal -->
    <div
      v-if="showDeleteModal"
      class="modal-overlay"
      @click.self="
        showDeleteModal = false
      "
    >
      <div class="delete-modal">
        <div class="delete-icon">
          <i class="bx bx-trash"></i>
        </div>

        <h2>Delete Story?</h2>

        <p>
          Are you sure you want to delete

          <strong>
            {{ selectedStory?.story_title }}
          </strong>

          ? This action cannot be undone.
        </p>

        <div class="delete-actions">
          <button
            class="secondary-btn"
            @click="
              showDeleteModal = false
            "
          >
            Cancel
          </button>

          <button
            class="danger-btn"
            @click="deleteStory"
          >
            Delete Story
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {
  getStoriesApi,
  getStoryStatsApi,
  getStoryApi,
  createStoryApi,
  updateStoryApi,
  deleteStoryApi,
} from "@/apis/storyapis";

import {
  getCoursesApi,
} from "@/apis/courseapis";

import {
  getCreatorsApi,
} from "@/apis/creatorapis";

import {
  getBlogsApi,
} from "@/apis/blogapis";

export default {
  name: "StoriesScreen",

  data() {
    return {
      // =====================================================
      // FILTERS
      // =====================================================

      search: "",
      typeFilter: "",
      nicheFilter: "",

      // =====================================================
      // PAGINATION
      // =====================================================

      currentPage: 1,
      perPage: 8,
      totalStories: 0,
      totalPagesCount: 1,

      // =====================================================
      // DATA
      // =====================================================

      stories: [],

      // =====================================================
      // PARENT DATA
      // =====================================================

      courses: [],
      creators: [],
      blogs: [],

      // =====================================================
      // FILTER OPTIONS
      // =====================================================

      niches: [],

      // =====================================================
      // STATS
      // =====================================================

      stats: {
        totalStories: 0,
        creatorCount: 0,
        videoStories: 0,
        totalListeners: 0,
      },

      // =====================================================
      // LOADING
      // =====================================================

      loading: false,
      saving: false,
      deleting: false,

      loadingCourses: false,
      loadingCreators: false,
      loadingBlogs: false,

      // =====================================================
      // MODALS
      // =====================================================

      showFormModal: false,
      showViewModal: false,
      showDeleteModal: false,

      isEditing: false,

      selectedStory: null,

      // =====================================================
      // FORM
      // =====================================================

      form: {
        story_id: null,
        story_title: "",
        story_meta_title: "",
        story_keyword: "",
        story_parent: null,
        story_parent_type: "",
        story_niche: "",
        story_handle: "",
        story_typ: "",
        story_listners: "",
        story_price: null,
        story_creator: null,
        story_img: "",
        story_video: "",
      },
    };
  },

  // =========================================================
  // COMPUTED
  // =========================================================

  computed: {
    // =====================================================
    // TEMPLATE COMPATIBILITY
    // =====================================================

    paginatedStories() {
      return this.stories;
    },

    filteredStories() {
      return this.stories;
    },

    totalPages() {
      return this.totalPagesCount;
    },

    // =====================================================
    // PAGINATION RANGE
    // =====================================================

    startItem() {
      if (!this.totalStories) {
        return 0;
      }

      return (
        (this.currentPage - 1) *
          this.perPage +
        1
      );
    },

    endItem() {
      return Math.min(
        this.currentPage * this.perPage,
        this.totalStories
      );
    },

    // =====================================================
    // STATS
    // =====================================================

    creatorCount() {
      return Number(
        this.stats.creatorCount || 0
      );
    },

    videoStories() {
      return Number(
        this.stats.videoStories || 0
      );
    },

    totalListeners() {
      return Number(
        this.stats.totalListeners || 0
      ).toLocaleString("en-IN");
    },
  },

  // =========================================================
  // WATCHERS
  // =========================================================

  watch: {
    // =====================================================
    // SEARCH
    // =====================================================

    search() {
      this.currentPage = 1;
      this.loadStories();
    },

    // =====================================================
    // TYPE FILTER
    // =====================================================

    typeFilter() {
      this.currentPage = 1;
      this.loadStories();
    },

    // =====================================================
    // NICHE FILTER
    // =====================================================

    nicheFilter() {
      this.currentPage = 1;
      this.loadStories();
    },

    // =====================================================
    // PARENT TYPE
    // =====================================================

    "form.story_parent_type"(value) {
      /*
       * Home does not require any parent.
       */
      if (value === "Home") {
        this.form.story_parent = 0;
        return;
      }

      /*
       * When changing from one parent type
       * to another, reset the selected parent.
       */
      this.form.story_parent = null;
    },
  },

  // =========================================================
  // MOUNTED
  // =========================================================

  mounted() {
    this.loadStories();
    this.loadStats();

    // Parent dropdown data
    this.loadCourses();
    this.loadCreators();
    this.loadBlogs();
  },

  // =========================================================
  // METHODS
  // =========================================================

  methods: {
    // =====================================================
    // LOAD STORIES
    // =====================================================

    async loadStories() {
      this.loading = true;

      try {
        const res = await getStoriesApi({
          page: this.currentPage,
          limit: this.perPage,
          search: this.search,
          type: this.typeFilter,
          niche: this.nicheFilter,
        });

        if (
          res &&
          res.status !== false
        ) {
          this.stories =
            res.stories || [];

          this.totalStories = Number(
            res.total || 0
          );

          this.totalPagesCount = Math.max(
            Number(
              res.totalPages || 1
            ),
            1
          );

          this.currentPage = Number(
            res.page ||
              this.currentPage
          );

          // -----------------------------------------------
          // NICHE OPTIONS
          // -----------------------------------------------

          if (
            Array.isArray(res.niches)
          ) {
            this.niches =
              res.niches;
          } else if (
            !this.niches.length
          ) {
            this.buildNiches();
          }

          // -----------------------------------------------
          // INVALID PAGE
          // -----------------------------------------------

          if (
            this.currentPage >
            this.totalPagesCount
          ) {
            this.currentPage =
              this.totalPagesCount;

            await this.loadStories();
          }
        } else {
          this.stories = [];
          this.totalStories = 0;
          this.totalPagesCount = 1;
        }
      } catch (error) {
        console.error(
          "Load Stories Error:",
          error
        );

        this.stories = [];
        this.totalStories = 0;
        this.totalPagesCount = 1;
      } finally {
        this.loading = false;
      }
    },

    // =====================================================
    // LOAD STATS
    // =====================================================

    async loadStats() {
      try {
        const res =
          await getStoryStatsApi();

        if (
          res &&
          res.status !== false &&
          res.stats
        ) {
          this.stats = {
            ...this.stats,
            ...res.stats,
          };
        }
      } catch (error) {
        console.error(
          "Load Story Stats Error:",
          error
        );
      }
    },

    // =====================================================
    // LOAD COURSES
    // =====================================================

    async loadCourses() {
      this.loadingCourses = true;

      try {
        const res =
          await getCoursesApi({
            page: 1,
            limit: 1000,
          });

        if (
          res &&
          res.status !== false
        ) {
          this.courses =
            res.courses ||
            res.data ||
            [];
        } else {
          this.courses = [];
        }
      } catch (error) {
        console.error(
          "Load Courses Error:",
          error
        );

        this.courses = [];
      } finally {
        this.loadingCourses = false;
      }
    },

    // =====================================================
    // LOAD CREATORS
    // =====================================================

    async loadCreators() {
      this.loadingCreators = true;

      try {
        const res =
          await getCreatorsApi({
            page: 1,
            limit: 1000,
          });

        if (
          res &&
          res.status !== false
        ) {
          this.creators =
            res.creators ||
            res.data ||
            [];
        } else {
          this.creators = [];
        }
      } catch (error) {
        console.error(
          "Load Creators Error:",
          error
        );

        this.creators = [];
      } finally {
        this.loadingCreators = false;
      }
    },

    // =====================================================
    // LOAD BLOGS
    // =====================================================

    async loadBlogs() {
      this.loadingBlogs = true;

      try {
        const res =
          await getBlogsApi({
            page: 1,
            limit: 1000,
          });

        if (
          res &&
          res.status !== false
        ) {
          this.blogs =
            res.blogs ||
            res.data ||
            [];
        } else {
          this.blogs = [];
        }
      } catch (error) {
        console.error(
          "Load Blogs Error:",
          error
        );

        this.blogs = [];
      } finally {
        this.loadingBlogs = false;
      }
    },

    // =====================================================
    // BUILD NICHE OPTIONS
    // =====================================================

    buildNiches() {
      this.niches = [
        ...new Set(
          this.stories
            .map(
              (story) =>
                story.story_niche
            )
            .filter(Boolean)
        ),
      ].sort();
    },

    // =====================================================
    // DATE
    // =====================================================

    formatDate(date) {
      if (!date) {
        return "—";
      }

      const value =
        new Date(date);

      if (
        Number.isNaN(
          value.getTime()
        )
      ) {
        return date;
      }

      return value.toLocaleDateString(
        "en-IN",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      );
    },

    // =====================================================
    // RESET FILTERS
    // =====================================================

    resetFilters() {
      this.search = "";
      this.typeFilter = "";
      this.nicheFilter = "";
      this.currentPage = 1;

      this.$nextTick(() => {
        this.loadStories();
      });
    },

    // =====================================================
    // EMPTY FORM
    // =====================================================

    getEmptyForm() {
      return {
        story_id: null,
        story_title: "",
        story_meta_title: "",
        story_keyword: "",
        story_parent: null,
        story_parent_type: "",
        story_niche: "",
        story_handle: "",
        story_typ: "",
        story_listners: "",
        story_price: null,
        story_creator: null,
        story_img: "",
        story_video: "",
      };
    },

    // =====================================================
    // ADD STORY
    // =====================================================

    openAddModal() {
      this.isEditing = false;

      this.selectedStory = null;

      this.form =
        this.getEmptyForm();

      this.showFormModal = true;
    },

    // =====================================================
    // CLOSE FORM
    // =====================================================

    closeFormModal() {
      if (this.saving) {
        return;
      }

      this.showFormModal = false;

      this.isEditing = false;

      this.selectedStory = null;

      this.form =
        this.getEmptyForm();
    },

    // =====================================================
    // VIEW STORY
    // =====================================================

    async viewStory(story) {
      if (!story?.story_id) {
        return;
      }

      try {
        const res =
          await getStoryApi(
            story.story_id
          );

        if (
          res &&
          res.status !== false &&
          res.story
        ) {
          this.selectedStory =
            res.story;
        } else {
          this.selectedStory =
            story;
        }

        this.showViewModal = true;
      } catch (error) {
        console.error(
          "View Story Error:",
          error
        );

        this.selectedStory =
          story;

        this.showViewModal = true;
      }
    },

    // =====================================================
    // EDIT STORY
    // =====================================================

    async editStory(story) {
      if (!story?.story_id) {
        return;
      }

      try {
        const res =
          await getStoryApi(
            story.story_id
          );

        const data =
          res &&
          res.status !== false &&
          res.story
            ? res.story
            : story;

        this.selectedStory =
          data;

        this.isEditing = true;

        let parentType =
          data.story_parent_type ||
          "";

        let parentId =
          data.story_parent;

        /*
         * Home always uses 0.
         */
        if (
          parentType === "Home"
        ) {
          parentId = 0;
        } else {
          parentId =
            parentId !== null &&
            parentId !== undefined &&
            parentId !== ""
              ? Number(parentId)
              : null;
        }

        this.form = {
          ...this.getEmptyForm(),

          story_id:
            data.story_id,

          story_title:
            data.story_title || "",

          story_meta_title:
            data.story_meta_title ||
            "",

          story_keyword:
            data.story_keyword ||
            "",

          story_parent:
            parentId,

          story_parent_type:
            parentType,

          story_niche:
            data.story_niche || "",

          story_handle:
            data.story_handle || "",

          story_typ:
            data.story_typ || "",

          story_listners:
            data.story_listners || "",

          story_price:
            data.story_price !== null &&
            data.story_price !==
              undefined
              ? Number(
                  data.story_price
                )
              : null,

          story_creator:
            data.story_creator !==
              null &&
            data.story_creator !==
              undefined &&
            data.story_creator !==
              ""
              ? Number(
                  data.story_creator
                )
              : null,

          story_img:
            data.story_img || "",

          story_video:
            data.story_video || "",
        };

        this.showViewModal = false;
        this.showFormModal = true;
      } catch (error) {
        console.error(
          "Edit Story Error:",
          error
        );
      }
    },

    // =====================================================
    // VALIDATE PARENT
    // =====================================================

    validateParent() {
      const parentType =
        this.form
          .story_parent_type;

      /*
       * Parent Type is required.
       */
      if (!parentType) {
        alert(
          "Please select parent type."
        );

        return false;
      }

      /*
       * Home does not need a parent.
       */
      if (
        parentType === "Home"
      ) {
        this.form.story_parent = 0;

        return true;
      }

      /*
       * Course / Creator / Blog
       * require selected parent.
       */
      if (
        this.form.story_parent ===
          null ||
        this.form.story_parent ===
          undefined ||
        this.form.story_parent ===
          ""
      ) {
        alert(
          `Please select ${parentType}.`
        );

        return false;
      }

      return true;
    },

    // =====================================================
    // SAVE STORY
    // =====================================================

    async saveStory() {
      // ---------------------------------------------------
      // STORY TITLE
      // ---------------------------------------------------

      if (
        !this.form.story_title ||
        !this.form.story_title.trim()
      ) {
        alert(
          "Please enter story title."
        );

        return;
      }

      // ---------------------------------------------------
      // PARENT VALIDATION
      // ---------------------------------------------------

      if (!this.validateParent()) {
        return;
      }

      // ---------------------------------------------------
      // NORMALIZE PARENT
      // ---------------------------------------------------

      if (
        this.form.story_parent_type ===
        "Home"
      ) {
        this.form.story_parent = 0;
      } else {
        this.form.story_parent =
          Number(
            this.form.story_parent
          );
      }

      // ---------------------------------------------------
      // NORMALIZE CREATOR
      // ---------------------------------------------------

      if (
        this.form.story_creator !==
          null &&
        this.form.story_creator !==
          undefined &&
        this.form.story_creator !==
          ""
      ) {
        this.form.story_creator =
          Number(
            this.form.story_creator
          );
      } else {
        this.form.story_creator =
          null;
      }

      this.saving = true;

      try {
        let res;

        // -------------------------------------------------
        // UPDATE
        // -------------------------------------------------

        if (this.isEditing) {
          res =
            await updateStoryApi(
              this.form
            );
        }

        // -------------------------------------------------
        // CREATE
        // -------------------------------------------------

        else {
          res =
            await createStoryApi(
              this.form
            );
        }

        // -------------------------------------------------
        // SUCCESS
        // -------------------------------------------------

        if (
          res &&
          res.status !== false
        ) {
          alert(
            res.success ||
              (
                this.isEditing
                  ? "Story updated successfully."
                  : "Story created successfully."
              )
          );

          this.showFormModal = false;

          this.isEditing = false;

          this.selectedStory = null;

          this.form =
            this.getEmptyForm();

          await this.loadStories();
          await this.loadStats();
        }

        // -------------------------------------------------
        // API ERROR
        // -------------------------------------------------

        else {
          alert(
            res?.success ||
              "Unable to save story."
          );
        }
      } catch (error) {
        console.error(
          "Save Story Error:",
          error
        );

        alert(
          error?.response?.data
            ?.success ||
            "Something went wrong while saving story."
        );
      } finally {
        this.saving = false;
      }
    },

    // =====================================================
    // CONFIRM DELETE
    // =====================================================

    confirmDelete(story) {
      if (!story?.story_id) {
        return;
      }

      this.selectedStory =
        story;

      this.showDeleteModal = true;
    },

    // =====================================================
    // DELETE STORY
    // =====================================================

    async deleteStory() {
      if (
        !this.selectedStory?.story_id
      ) {
        return;
      }

      this.deleting = true;

      try {
        const res =
          await deleteStoryApi(
            this.selectedStory
              .story_id
          );

        if (
          res &&
          res.status !== false
        ) {
          alert(
            res.success ||
              "Story deleted successfully."
          );

          this.showDeleteModal = false;

          this.selectedStory = null;

          /*
           * If deleted story was the
           * only record on this page,
           * move to previous page.
           */
          if (
            this.stories.length ===
              1 &&
            this.currentPage > 1
          ) {
            this.currentPage--;
          }

          await this.loadStories();
          await this.loadStats();
        } else {
          alert(
            res?.success ||
              "Story deletion failed."
          );
        }
      } catch (error) {
        console.error(
          "Delete Story Error:",
          error
        );

        alert(
          error?.response?.data
            ?.success ||
            "Something went wrong while deleting story."
        );
      } finally {
        this.deleting = false;
      }
    },

    // =====================================================
    // PAGINATION
    // =====================================================

    goToPage(page) {
      if (
        page < 1 ||
        page >
          this.totalPagesCount ||
        page === this.currentPage
      ) {
        return;
      }

      this.currentPage = page;

      this.loadStories();
    },

    previousPage() {
      if (
        this.currentPage <= 1
      ) {
        return;
      }

      this.currentPage--;

      this.loadStories();
    },

    nextPage() {
      if (
        this.currentPage >=
        this.totalPagesCount
      ) {
        return;
      }

      this.currentPage++;

      this.loadStories();
    },
  },
};
</script>

<style scoped>
.stories-wrapper {
  padding: 24px;
  background: #f8fafc;
  min-height: calc(100vh - 70px);
  color: #0f172a;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 22px;
}

.page-header h1 {
  margin: 0 0 5px;
  font-size: 25px;
  font-weight: 700;
}

.page-header p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}

/* Buttons */
.primary-btn,
.secondary-btn,
.reset-btn,
.danger-btn {
  height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: 0.2s;
}

.primary-btn {
  background: #2563eb;
  color: white;
}

.primary-btn:hover {
  background: #1d4ed8;
}

.secondary-btn {
  background: white;
  color: #334155;
  border-color: #e2e8f0;
}

.secondary-btn:hover {
  background: #f8fafc;
}

.reset-btn {
  background: white;
  color: #475569;
  border-color: #e2e8f0;
}

.reset-btn:hover {
  background: #f8fafc;
}

.danger-btn {
  background: #dc2626;
  color: white;
}

.danger-btn:hover {
  background: #b91c1c;
}

/* Stats */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 17px;
  display: flex;
  align-items: center;
  gap: 13px;
}

.stat-icon {
  width: 43px;
  height: 43px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 21px;
}

.stat-icon.blue {
  background: #eff6ff;
  color: #2563eb;
}

.stat-icon.green {
  background: #f0fdf4;
  color: #16a34a;
}

.stat-icon.orange {
  background: #fff7ed;
  color: #ea580c;
}

.stat-icon.purple {
  background: #faf5ff;
  color: #9333ea;
}

.stat-card span {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.stat-card strong {
  font-size: 21px;
}

/* Card */
.content-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

/* Toolbar */
.toolbar {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #e2e8f0;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 380px;
}

.search-box i {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
}

.search-box input,
.filter-select {
  height: 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  outline: none;
  font-size: 13px;
  color: #334155;
  background: white;
}

.search-box input {
  width: 100%;
  padding: 0 13px 0 39px;
}

.filter-select {
  min-width: 145px;
  padding: 0 12px;
}

.search-box input:focus,
.filter-select:focus,
.form-group input:focus,
.form-group select:focus {
  border-color: #93c5fd;
}

/* Table */
.table-wrapper {
  overflow-x: auto;
}

table {
  width: 100%;
  min-width: 1150px;
  border-collapse: collapse;
}

th {
  background: #f8fafc;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  text-align: left;
  padding: 13px 16px;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

td {
  padding: 13px 16px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  font-size: 13px;
}

tbody tr:hover {
  background: #fafcff;
}

/* Story */
.story-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 250px;
}

.story-image {
  width: 48px;
  height: 40px;
  border-radius: 7px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.story-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.story-details strong {
  display: block;
  max-width: 230px;
  font-size: 12px;
  line-height: 1.35;
}

.story-details small {
  display: block;
  color: #94a3b8;
  font-size: 10px;
  margin-top: 3px;
}

.niche-badge,
.type-badge {
  display: inline-flex;
  padding: 5px 9px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
}

.niche-badge {
  background: #f1f5f9;
  color: #475569;
}

.type-badge {
  background: #eff6ff;
  color: #2563eb;
}

.creator-info strong {
  display: block;
  font-size: 12px;
}

.creator-info small {
  display: block;
  margin-top: 3px;
  color: #94a3b8;
  font-size: 10px;
}

.listeners {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #475569;
  font-size: 12px;
  white-space: nowrap;
}

.listeners i {
  color: #64748b;
}

.price {
  color: #334155;
  font-size: 12px;
  white-space: nowrap;
}

.media-list {
  display: flex;
  gap: 5px;
}

.media-list span {
  width: 27px;
  height: 27px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
}

.media-list .no-media {
  color: #94a3b8;
}

.date-text {
  color: #64748b;
  font-size: 11px;
  white-space: nowrap;
}

/* Actions */
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 5px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
}

.icon-btn.view {
  color: #64748b;
}

.icon-btn.view:hover {
  background: #f1f5f9;
}

.icon-btn.edit {
  color: #2563eb;
}

.icon-btn.edit:hover {
  background: #eff6ff;
}

.icon-btn.delete {
  color: #dc2626;
}

.icon-btn.delete:hover {
  background: #fef2f2;
}

/* Empty */
.empty-state {
  text-align: center;
  padding: 60px 20px !important;
  color: #94a3b8;
}

.empty-state i {
  font-size: 42px;
}

.empty-state h3 {
  margin: 10px 0 4px;
  color: #334155;
  font-size: 15px;
}

.empty-state p {
  margin: 0;
  font-size: 13px;
}

/* Pagination */
.pagination {
  padding: 14px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #64748b;
  font-size: 12px;
}

.pagination-buttons {
  display: flex;
  gap: 5px;
}

.pagination-buttons button {
  min-width: 31px;
  height: 31px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  color: #475569;
}

.pagination-buttons button.active {
  background: #2563eb;
  border-color: #2563eb;
  color: white;
}

.pagination-buttons button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.48);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal {
  width: 760px;
  max-width: 100%;
  max-height: 90vh;
  background: white;
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.view-modal {
  width: 700px;
}

.modal-header {
  padding: 20px 22px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.modal-header h2 {
  margin: 0 0 4px;
  font-size: 19px;
}

.modal-header p {
  margin: 0;
  color: #64748b;
  font-size: 12px;
}

.close-btn {
  width: 34px;
  height: 34px;
  border: none;
  background: #f8fafc;
  border-radius: 7px;
  font-size: 20px;
  color: #64748b;
  cursor: pointer;
}

.modal-body {
  padding: 22px;
  overflow-y: auto;
}

.modal-footer {
  padding: 15px 22px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 9px;
}

/* Form */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.form-group.full {
  grid-column: 1 / -1;
}

.form-group label {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
}

.form-group label span {
  color: #dc2626;
}

.form-group input,
.form-group select {
  width: 100%;
  box-sizing: border-box;
  height: 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0 11px;
  outline: none;
  font-size: 13px;
  color: #334155;
  background: white;
}

/* View */
.story-profile {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.large-story-image {
  width: 75px;
  height: 65px;
  flex-shrink: 0;
  border-radius: 9px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-size: 24px;
}

.large-story-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.story-profile h3 {
  margin: 0 0 3px;
  font-size: 18px;
}

.story-profile p {
  margin: 0 0 7px;
  color: #94a3b8;
  font-size: 12px;
}

.story-badges {
  display: flex;
  gap: 6px;
}

.detail-section {
  margin-bottom: 22px;
}

.detail-section h4 {
  margin: 0 0 12px;
  font-size: 13px;
  color: #334155;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.detail-item {
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.detail-item span {
  display: block;
  color: #94a3b8;
  font-size: 11px;
  margin-bottom: 5px;
}

.detail-item strong {
  display: block;
  color: #334155;
  font-size: 12px;
  word-break: break-word;
}

.media-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.media-preview > div {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.media-preview > div > span {
  color: #64748b;
  font-size: 11px;
}

.media-preview img {
  width: 130px;
  height: 85px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.video-link {
  height: 38px;
  padding: 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #2563eb;
  text-decoration: none;
  font-size: 12px;
}

.no-data {
  color: #94a3b8;
  font-size: 12px;
}

/* Delete */
.delete-modal {
  width: 400px;
  max-width: 100%;
  background: white;
  border-radius: 14px;
  padding: 28px;
  text-align: center;
}

.delete-icon {
  width: 54px;
  height: 54px;
  margin: 0 auto 15px;
  border-radius: 50%;
  background: #fef2f2;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.delete-modal h2 {
  margin: 0 0 8px;
  font-size: 19px;
}

.delete-modal p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}

.delete-actions {
  margin-top: 22px;
  display: flex;
  justify-content: center;
  gap: 9px;
}

/* Responsive */
@media (max-width: 1100px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .toolbar {
    flex-wrap: wrap;
  }

  .search-box {
    max-width: none;
    flex-basis: 100%;
  }
}

@media (max-width: 700px) {
  .stories-wrapper {
    padding: 16px;
  }

  .page-header {
    align-items: flex-start;
    gap: 15px;
  }

  .page-header h1 {
    font-size: 21px;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    width: 100%;
  }

  .filter-select,
  .reset-btn {
    width: 100%;
  }

  .form-grid,
  .details-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full {
    grid-column: auto;
  }

  .pagination {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}
</style>